/**
 * Push Events to Kafka API
 * 
 * This API is used to send events to Kafka for tracking user actions:
 * - sign_up: When user completes signup
 * - trial_started: When user takes ₹2 trial
 * - subscribe_started: When user subscribes via mandate payment
 */

import axios from 'axios';
import { getDeviceId } from '../commonApi';
import { getUTMParamsFromURL, getTitleId } from '../../utils/utmTracker';
import { useTokenStore } from '../../storage/tokenState';
import { getCachedUserIP, initializeUserIP } from '../../utils/cleverTapHelper';

// Super API base URL for Kafka events (use env variable like other APIs)
const SUPER_API_BASE_URL = process.env.REACT_APP_API_SIGNUP || 'https://super-api.zupeestudio.com';
//https://super-api.zupeestudio.com
const PUSH_KAFKA_ENDPOINT = '/v1.0/zupeeTv/v1/pushEventsToKafka';

// Event types
export type KafkaEventSubType = 'sign_up' | 'WEB_EVENT_LOGIN' | 'trial_started' | 'subscribe_started';

// Interface for user data in Kafka event
interface KafkaUserData {
  appVersion: string;
  deviceId: string;
  gAId: string;
  packageName: string;
  userId: string;
  userName: string;
  zupeeUserId?: string;
  userIP?: string;
}

// Interface for URL params in Kafka event
interface KafkaUrlParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_adset?: string;
  utm_ad?: string;
  utm_id?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
  titleId?: string;
}

// Interface for Kafka event payload
interface KafkaEventPayload {
  eventName: string;
  eventTime: number;
  eventType: string;
  eventSubType: KafkaEventSubType;
  userData: KafkaUserData;
  urlParams: KafkaUrlParams;
  paymentAmount?: number; // Required for trial_started and subscribe_started events
}

// Interface for API response
interface KafkaEventResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Get Google Analytics ID from cookies or generate one
 */
const getGAId = (): string => {
  // Try to get GA client ID from _ga cookie
  const gaCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('_ga='));
  
  if (gaCookie) {
    // GA cookie format: GA1.2.XXXXXXXXXX.XXXXXXXXXX
    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  
  // Fallback: Try to get from localStorage
  let gaId = localStorage.getItem('gaClientId');
  if (!gaId) {
    // Generate a new UUID-like ID
    gaId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('gaClientId', gaId);
  }
  
  return gaId;
};

/**
 * Push event to Kafka
 * 
 * @param eventSubType - Type of event: 'sign_up', 'trial_started', or 'subscribe_started'
 * @param userId - User ID from signup response
 * @param mobileNumber - User's mobile number (will be prefixed with +91 if not already)
 * @param zupeeUserId - Optional Zupee User ID from authenticateUser response
 * @param paymentAmount - Optional payment amount (required for trial_started and subscribe_started)
 * @returns Promise with API response
 */
export const pushKafkaEvent = async (
  eventSubType: KafkaEventSubType,
  userId: string,
  mobileNumber: string,
  zupeeUserId?: string | number,
  paymentAmount?: number
): Promise<KafkaEventResponse> => {
  try {
    // Format mobile number with country code
    let formattedMobile = mobileNumber;
    if (!mobileNumber.startsWith('+')) {
      if (mobileNumber.startsWith('91')) {
        formattedMobile = `+${mobileNumber}`;
      } else {
        formattedMobile = `+91${mobileNumber}`;
      }
    }

    // Get device ID
    const deviceId = getDeviceId();

    // Get Google Analytics ID
    const gAId = getGAId();

    // Get UTM params from URL or sessionStorage
    const utmParams = getUTMParamsFromURL();
    
    // Get titleId
    const titleId = getTitleId();

    // Build URL params
    const urlParams: KafkaUrlParams = {};
    if (utmParams.utm_source) urlParams.utm_source = utmParams.utm_source;
    if (utmParams.utm_medium) urlParams.utm_medium = utmParams.utm_medium;
    if (utmParams.utm_campaign) urlParams.utm_campaign = utmParams.utm_campaign;
    if (utmParams.utm_adset) urlParams.utm_adset = utmParams.utm_adset;
    if (utmParams.utm_ad) urlParams.utm_ad = utmParams.utm_ad;
    if (utmParams.utm_id) urlParams.utm_id = utmParams.utm_id;
    if (utmParams.utm_content) urlParams.utm_content = utmParams.utm_content;
    if (utmParams.utm_term) urlParams.utm_term = utmParams.utm_term;
    if (utmParams.fbclid) urlParams.fbclid = utmParams.fbclid;
    if (utmParams.gclid) urlParams.gclid = utmParams.gclid;
    if (titleId) urlParams.titleId = titleId;

    // Get user IP from cache (includes IP for all Kafka events)
    const userIP = getCachedUserIP();
    
    // If IP is not cached, try to initialize it for future events
    if (!userIP) {
      initializeUserIP();
    }

    // Build userData (includes user IP if available)
    const userData: KafkaUserData = {
      appVersion: process.env.REACT_APP_VERSION || '1009',
      deviceId: deviceId,
      gAId: gAId,
      packageName: 'com.eight.network.android.vertical',
      userId: userId,
      userName: formattedMobile,
      ...(userIP && { userIP: userIP }),
      ...(zupeeUserId && { zupeeUserId: zupeeUserId.toString() })
    };

    // Build payload
    const payload: KafkaEventPayload = {
      eventName: 'ZUPEE_TV_EVENT',
      eventTime: Date.now(),
      eventType: 'campaign_details',
      eventSubType: eventSubType,
      userData: userData,
      urlParams: urlParams
    };

    // Add paymentAmount for trial_started and subscribe_started events
    if ((eventSubType === 'trial_started' || eventSubType === 'subscribe_started') && paymentAmount !== undefined) {
      payload.paymentAmount = paymentAmount;
    }

    console.log(`[KafkaEvent] Pushing ${eventSubType} event:`, payload);

    // Get user token for authorization
    const token = useTokenStore.getState().token;

    // Wrap payload in events object with array as required by the API
    const requestBody = {
      events: [payload]
    };

    // Make API call
    const response = await axios.post(
      `${SUPER_API_BASE_URL}${PUSH_KAFKA_ENDPOINT}`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token?.accessToken || '',
          'x-zupee-source': 'web',
          // 'x-zupee-env': 'cse',
          // "z-stage-env-id":"stv-prya80a3bac",
          'Package-Name': process.env.REACT_APP_APPPACKAGENAME || 'pwa.verticalbyzupee.com',
          'appname': process.env.REACT_APP_APP_NAME || 'pwa.verticalbyzupee.com',
        },
        timeout: 10000 // 10 second timeout
      }
    );

    console.log(`[KafkaEvent] ${eventSubType} event pushed successfully:`, response.data);

    return {
      success: true,
      message: 'Event pushed successfully'
    };
  } catch (error: any) {
    console.error(`[KafkaEvent] Failed to push ${eventSubType} event:`, error);
    
    // Don't throw error - this is a non-critical tracking call
    // Just return failure response
    return {
      success: false,
      error: error.message || 'Failed to push event'
    };
  }
};

/**
 * Hook for pushing Kafka events
 * Returns a function that can be called to push events
 */
const usePushKafkaEvent = () => {
  /**
   * Push sign_up or WEB_EVENT_LOGIN event based on isNewSignup flag
   * @param isNewSignup - If true, fires 'sign_up', if false, fires 'WEB_EVENT_LOGIN'
   */
  const pushSignUpEvent = async (userId: string, mobileNumber: string, zupeeUserId?: string | number, isNewSignup?: boolean) => {
    const eventSubType: KafkaEventSubType = isNewSignup === true ? 'sign_up' : 'WEB_EVENT_LOGIN';
    return pushKafkaEvent(eventSubType, userId, mobileNumber, zupeeUserId);
  };

  /**
   * Push trial_started event after ₹2 trial payment
   * @param paymentAmount - The trial payment amount (e.g., 2 for ₹2 trial)
   */
  const pushTrialStartedEvent = async (userId: string, mobileNumber: string, zupeeUserId?: string | number, paymentAmount?: number) => {
    return pushKafkaEvent('trial_started', userId, mobileNumber, zupeeUserId, paymentAmount);
  };

  /**
   * Push subscribe_started event after mandate/subscription payment
   * @param paymentAmount - The subscription/mandate payment amount
   */
  const pushSubscribeStartedEvent = async (userId: string, mobileNumber: string, zupeeUserId?: string | number, paymentAmount?: number) => {
    return pushKafkaEvent('subscribe_started', userId, mobileNumber, zupeeUserId, paymentAmount);
  };

  return {
    pushSignUpEvent,
    pushTrialStartedEvent,
    pushSubscribeStartedEvent,
    pushKafkaEvent
  };
};

export default usePushKafkaEvent;
